import React, { useEffect, useState } from "react";
import LightBar from "./LightBar";
import DecorativeLines from "./DecorativeLines";

const phrases = [
  "A JavaScript Enthusiast",
  "A Frontend Developer",
  "A Backend Developer",
  "A QA Engineer",  
  "A Full Stack Developer",
];

const Header = () => {
  const [currentPhrase, setCurrentPhrase] = useState("");
  const [phraseIndex, setPhraseIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [showCursor, setShowCursor] = useState(true);
  const headingRef = React.useRef(null);

  useEffect(() => {
    if (phraseIndex < phrases.length) {
      if (charIndex < phrases[phraseIndex].length) {
        const timeout = setTimeout(() => {
          setCurrentPhrase((prev) => prev + phrases[phraseIndex][charIndex]);
          setCharIndex((prev) => prev + 1);
        }, 60);
        return () => clearTimeout(timeout);
      } else {
        const timeout = setTimeout(() => {
          setPhraseIndex((prev) => (prev + 1) % phrases.length);
          setCharIndex(0);
          setCurrentPhrase("");
        }, 1000);
        return () => clearTimeout(timeout);
      }
    }
  }, [charIndex, phraseIndex]);

  useEffect(() => {
    const interval = setInterval(() => {
      setShowCursor((c) => !c);
    }, 500);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="flex flex-col items-center justify-center min-h-[60vh] text-cyan-300 relative bg-white dark:bg-[#232946] px-4 md:px-0">
      <div className="absolute top-0 left-0 z-0 pointer-events-none">
        <div className="w-32 h-32 rounded-full rainbow-gradient-animate opacity-70"></div>
      </div>
      <div className="absolute top-0 right-0 z-0 pointer-events-none">
        <div className="w-32 h-32 rounded-full rainbow-gradient-animate opacity-70"></div>
      </div>
      <div className="absolute top-1/3 left-12 z-0 pointer-events-none">
        <div className="w-16 h-16 rounded-full rainbow-gradient-animate opacity-60"></div>
      </div>
      <div className="absolute top-1/5 left-12 z-0 pointer-events-none">
        <div className="w-16 h-16 rounded-full rainbow-gradient-animate opacity-60"></div>
      </div>
      <div className="absolute top-1/3 right-12 z-0 pointer-events-none">
        <div className="w-16 h-16 rounded-full rainbow-gradient-animate opacity-60"></div>
      </div>
      <div className="absolute top-1/5 right-12 z-0 pointer-events-none">
        <div className="w-16 h-16 rounded-full rainbow-gradient-animate opacity-60"></div>
      </div>
      <DecorativeLines />
      <div className="relative w-44 h-44 mb-6 flex items-center justify-center z-10 mt-20">
        <div className="absolute inset-0 rounded-full bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-500 animate-spin-slow opacity-75 blur-sm"></div>
        
        <img
          src="https://res.cloudinary.com/dp7ltmmy8/image/upload/v1752208229/sourav_unwues.jpg"
          alt="Profile"
          className="w-40 h-40 rounded-full border-4 border-white dark:border-gray-800 shadow-2xl object-cover relative z-10 bg-white"
        />
        
        <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-gradient-to-r from-cyan-500 to-blue-600 px-4 py-1.5 rounded-full shadow-lg flex items-center gap-2 z-20 border-2 border-white dark:border-gray-800">
          <span className="text-white font-bold text-sm flex items-center gap-1">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M12.316 3.051a1 1 0 01.633 1.265l-4 12a1 1 0 11-1.898-.632l4-12a1 1 0 011.265-.633zM5.707 6.293a1 1 0 010 1.414L3.414 10l2.293 2.293a1 1 0 11-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0zm8.586 0a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 11-1.414-1.414L16.586 10l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
            Developer
          </span>
        </div>
      </div>
      
      <h1 ref={headingRef} className="text-4xl md:text-5xl font-bold mb-4 animated-gradient-text text-center">Hi I am Sourav Shetty</h1>
      <h2 className="text-lg md:text-xl font-medium text-gray-700 dark:text-gray-300 mb-2 z-20">
        Full Stack Software Engineer | AWS Certified
      </h2>
      
      <div className="mt-8 text-center z-20 flex flex-col items-center space-y-2 relative">
        <div className="absolute inset-0 flex justify-center items-center pointer-events-none -z-10">
          <div className="w-64 h-12 bg-gradient-to-r from-cyan-400 via-fuchsia-400 to-cyan-400 opacity-30 blur-xl animate-pulse" />
        </div>
        <span className="h-10 mt-2 text-lg md:text-[20px] font-bold text-black dark:text-white relative">
          {currentPhrase}
          <span className="inline-block w-2">{showCursor ? "|" : " "}</span>
        </span>
      </div>
      
      <p className="mt-6 text-lg max-w-2xl text-center z-10 text-black dark:text-white">
        Highly motivated and enthusiastic Full Stack Developer with experience in designing, developing and maintaining web applications using technologies such as JavaScript, React, Node.js, Java, AWS, SQL and NoSQL databases.
      </p>
      
      <div className="flex gap-6 mt-8 z-10">
        <a href="https://github.com/souravshetty" target="_blank" rel="noopener noreferrer" className="hover:text-blue-400 transition-colors">
          <svg width="32" height="32" fill="currentColor" viewBox="0 0 24 24"><path d="M12 0C5.37 0 0 5.373 0 12c0 5.303 3.438 9.8 8.205 11.387.6.113.82-.258.82-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.84 1.237 1.84 1.237 1.07 1.834 2.809 1.304 3.495.997.108-.775.418-1.305.762-1.605-2.665-.305-5.466-1.334-5.466-5.931 0-1.31.469-2.381 1.236-3.221-.124-.303-.535-1.523.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.984-.399 3.003-.404 1.018.005 2.047.138 3.006.404 2.291-1.553 3.297-1.23 3.297-1.23.653 1.653.242 2.873.119 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.803 5.624-5.475 5.921.43.372.823 1.102.823 2.222v3.293c0 .322.218.694.825.576C20.565 21.796 24 17.299 24 12c0-6.627-5.373-12-12-12z"/></svg>
        </a>
        <a href="https://www.linkedin.com/in/sourav-s-shetty/" target="_blank" rel="noopener noreferrer" className="hover:text-blue-400 transition-colors">
          <svg width="32" height="32" fill="currentColor" viewBox="0 0 24 24"><path d="M19 0h-14c-2.76 0-5 2.24-5 5v14c0 2.76 2.24 5 5 5h14c2.76 0 5-2.24 5-5v-14c0-2.76-2.24-5-5-5zm-11 19h-3v-10h3v10zm-1.5-11.28c-.966 0-1.75-.79-1.75-1.76 0-.97.784-1.76 1.75-1.76s1.75.79 1.75 1.76c0 .97-.784 1.76-1.75 1.76zm13.5 11.28h-3v-5.6c0-1.34-.03-3.07-1.87-3.07-1.87 0-2.16 1.46-2.16 2.97v5.7h-3v-10h2.89v1.36h.04c.4-.75 1.38-1.54 2.84-1.54 3.04 0 3.6 2 3.6 4.59v5.59z"/></svg>
        </a>
      </div>
      <div className="mt-4 flex flex-col items-center justify-center gap-3 z-10">
        <div className="flex items-center gap-2 bg-[#181f2a] rounded-xl px-4 py-2 shadow text-white font-medium text-sm md:text-base">
          <span className="relative flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
          </span>
          <span>Open for new opportunities as a <span className="font-bold text-white">DEVELOPER</span> <span className="text-gray-400">/</span> <span className="font-bold text-white">QA ENGINEER</span> ✨</span>
        </div>
      </div>
    </section>
  );
};

export default Header; 